import { LETTERS, fromAbsIndex, isWhite, noteLabel } from '../config/music';
import type { Settings } from '../state/settings';
import type { KeyBindings } from './keymap';
import { computeKeyBindings } from './keymap';
import { splitIntoRows } from './rows';

const WHITE_WIDTH_SCROLL = 46;
const WHITE_HEIGHT_BASE = 190;
/** Must match the `gap` on .piano. */
const ROW_GAP = 6;
/**
 * Key height follows key width, but the keyboard is pinned to the bottom of a
 * non-scrolling page, so on a short window it would squeeze the settings panel
 * down to a single row. It gets a share of the viewport instead.
 */
const KEYBOARD_VIEWPORT_SHARE = 0.42;
const KEYBOARD_MAX_HEIGHT = 380;
const KEYBOARD_MIN_HEIGHT = 120;

/** Bounds for a manual keyboard height (px): up to ~80% of the viewport. */
export const KEYBOARD_HEIGHT_MIN = KEYBOARD_MIN_HEIGHT;
export const keyboardHeightMax = (): number => Math.round(window.innerHeight * 0.8);

const keyboardHeightBudget = (manual: number | null): number => {
  if (manual !== null) return Math.max(KEYBOARD_HEIGHT_MIN, Math.min(keyboardHeightMax(), manual));
  return Math.max(KEYBOARD_MIN_HEIGHT, Math.min(KEYBOARD_MAX_HEIGHT, window.innerHeight * KEYBOARD_VIEWPORT_SHARE));
};

export interface KeyboardView {
  /** key element → absolute note index */
  readonly elToAbs: WeakMap<Element, number>;
  /** absolute note index → key element */
  readonly absToEl: ReadonlyMap<number, HTMLElement>;
  readonly bindings: KeyBindings;
}

let view: KeyboardView = {
  elToAbs: new WeakMap(),
  absToEl: new Map(),
  bindings: { byKey: new Map(), byNote: new Map(), lowAbs: null, highAbs: null, covered: 0, total: 0 },
};

export const currentView = (): KeyboardView => view;

/** The total keyboard height (px) used by the last render — the resize handle
 * starts its drag from here. */
let lastBudget = 0;
export const currentKeyboardHeight = (): number => lastBudget;

/** True when the last render made keys narrow enough to hide note-name labels
 * (letters / solfège / octave), so their toggles can be disabled. */
let lastCompact = false;
export const isCompactLayout = (): boolean => lastCompact;

interface Slot {
  readonly abs: number;
  readonly white: boolean;
}

function buildSlots(s: Settings): Slot[] {
  const out: Slot[] = [];
  for (let abs = s.startAbs; abs <= s.endAbs; abs++) {
    out.push({ abs, white: isWhite(fromAbsIndex(abs).noteIndex) });
  }
  return out;
}

function buildKeyEl(
  slot: Slot,
  kind: 'white' | 'black',
  width: number,
  height: number,
  compact: boolean,
  s: Settings,
  bindings: KeyBindings,
): HTMLElement {
  const el = document.createElement('div');
  el.className = `key ${kind}${compact ? ' compact' : ''}`;
  el.style.width = `${width}px`;
  el.style.height = `${height}px`;
  el.dataset['abs'] = String(slot.abs);

  const { octave: oct, noteIndex: idx } = fromAbsIndex(slot.abs);
  const keyChar = bindings.byNote.get(slot.abs);
  // Labelled but kept out of the tab order: 88 tab stops would bury the rest
  // of the page, and the computer keyboard is the real playing interface.
  el.setAttribute('role', 'button');
  el.setAttribute('tabindex', '-1');
  el.setAttribute('aria-pressed', 'false');
  el.setAttribute(
    'aria-label',
    `${LETTERS[idx]}${oct}${keyChar ? `, tasta ${keyChar}` : ''}`,
  );
  // The octave number sits at the very top of the key; note labels (letters /
  // solfège) carry no octave digit, so they stay narrow. Each label is
  // independent — a key can show any combination.
  if (s.showOctave) {
    const ov = document.createElement('div');
    ov.className = 'octave';
    ov.textContent = String(oct);
    el.appendChild(ov);
  }
  if (s.showKeys && keyChar) {
    const kc = document.createElement('div');
    kc.className = 'keychar';
    kc.textContent = keyChar === ' ' ? '␣' : keyChar;
    el.appendChild(kc);
  }
  if (s.showLetters) {
    const label = noteLabel(idx, 'letters');
    if (label) {
      const nn = document.createElement('div');
      nn.className = 'notename letters';
      nn.textContent = label;
      el.appendChild(nn);
    }
  }
  if (s.showSolfege) {
    const label = noteLabel(idx, 'solfege');
    if (label) {
      const nn = document.createElement('div');
      nn.className = 'notename solfege';
      nn.textContent = label;
      el.appendChild(nn);
    }
  }
  return el;
}

function layoutRow(
  rowEl: HTMLElement,
  slots: readonly Slot[],
  whiteWidth: number,
  heightCap: number,
  s: Settings,
  bindings: KeyBindings,
  elToAbs: WeakMap<Element, number>,
  absToEl: Map<number, HTMLElement>,
): void {
  const compact = whiteWidth < 24;
  if (compact) lastCompact = true;
  // In auto mode the key height follows a natural aspect ratio (capped), so it
  // never gets absurdly tall on a wide window. With a manual height the user is
  // explicitly asking the keys to FILL that height, so we use the full budget.
  const natural = Math.max(90, Math.min(WHITE_HEIGHT_BASE, whiteWidth * 4.2));
  const target = s.keyboardHeight !== null ? heightCap : Math.min(natural, heightCap);
  const whiteHeight = Math.max(48, target);
  const blackHeight = whiteHeight * 0.6;
  const blackWidth = Math.max(4, Math.round(whiteWidth * 0.6));
  const offset = blackWidth / 2;

  let whiteIndex = 0;
  const whites: Array<{ slot: Slot; index: number }> = [];
  const blacks: Array<{ slot: Slot; before: number }> = [];
  for (const slot of slots) {
    if (slot.white) whites.push({ slot, index: whiteIndex++ });
    else blacks.push({ slot, before: whiteIndex });
  }

  rowEl.style.width = `${offset * 2 + whiteIndex * whiteWidth}px`;
  rowEl.style.height = `${whiteHeight}px`;

  // One fragment, one insertion: with 88 keys this is the difference between a
  // single layout pass and roughly 250 of them.
  const frag = document.createDocumentFragment();
  for (const { slot, index } of whites) {
    const el = buildKeyEl(slot, 'white', whiteWidth, whiteHeight, compact, s, bindings);
    el.style.left = `${offset + index * whiteWidth}px`;
    frag.appendChild(el);
    elToAbs.set(el, slot.abs);
    absToEl.set(slot.abs, el);
  }
  for (const { slot, before } of blacks) {
    const el = buildKeyEl(slot, 'black', blackWidth, blackHeight, compact, s, bindings);
    el.style.left = `${offset + before * whiteWidth - blackWidth / 2}px`;
    frag.appendChild(el);
    elToAbs.set(el, slot.abs);
    absToEl.set(slot.abs, el);
  }
  rowEl.appendChild(frag);
}

const createRow = (): HTMLElement => {
  const row = document.createElement('div');
  row.className = 'piano-row';
  return row;
};

export function renderKeyboard(pianoEl: HTMLElement, scrollEl: HTMLElement, s: Settings): KeyboardView {
  const bindings = computeKeyBindings(s);
  const elToAbs = new WeakMap<Element, number>();
  const absToEl = new Map<number, HTMLElement>();
  const slots = buildSlots(s);

  // Split into rows first: the per-row height cap depends on how many there
  // are. The keymap uses the same split, so the row-aligned layout always
  // agrees with what is drawn.
  const byAbs = new Map(slots.map((slot) => [slot.abs, slot]));
  const rows: Slot[][] = splitIntoRows(s).map((row) =>
    row.map((abs) => byAbs.get(abs)).filter((slot): slot is Slot => slot !== undefined),
  );

  const budget = keyboardHeightBudget(s.keyboardHeight);
  lastBudget = budget;
  lastCompact = false; // set true by layoutRow if any row ends up compact
  const heightCap = (budget - ROW_GAP * (rows.length - 1)) / rows.length;

  scrollEl.style.overflowX = s.displayMode === 'scroll' ? 'auto' : 'hidden';
  const available = Math.max(200, scrollEl.clientWidth - 8);

  // splitIntoRows returns rows low-to-high in pitch, but on screen the higher
  // half belongs on top — "higher pitch, higher position", and it lines up with
  // the computer keyboard, whose lower physical rows (Z) play the bass half and
  // upper rows (Q) the treble. Rendering low-half-first would put bass on top
  // while the keys under your fingers say the opposite.
  const renderOrder = rows.length > 1 ? [...rows].reverse() : rows;

  const frag = document.createDocumentFragment();
  for (const row of renderOrder) {
    const whiteCount = row.filter((n) => n.white).length || 1;
    const whiteWidth =
      s.displayMode === 'scroll' ? WHITE_WIDTH_SCROLL : Math.max(5, Math.floor(available / whiteCount));
    const rowEl = createRow();
    layoutRow(rowEl, row, whiteWidth, heightCap, s, bindings, elToAbs, absToEl);
    frag.appendChild(rowEl);
  }

  pianoEl.replaceChildren(frag);
  view = { elToAbs, absToEl, bindings };
  return view;
}

export function setKeyActive(abs: number, active: boolean): void {
  const el = view.absToEl.get(abs);
  if (!el) return;
  el.classList.toggle('active', active);
  el.setAttribute('aria-pressed', String(active));
}

/** Re-apply highlighting after a re-render throws the old elements away. */
export function restoreActive(pitches: readonly number[]): void {
  for (const abs of pitches) setKeyActive(abs, true);
}
